/**
 * Calculate status check threshold (minutes) based on incident type and source.
 * Shared between Dashboard and MobileDashboard.
 *
 * @param {string} incidentType
 * @param {string} source
 * @param {{ defaultMinutes: number, overrides: Record<string, number> }} settings
 * @returns {number} threshold in minutes
 */
export const getStatusCheckThreshold = (incidentType, source, settings) => {
  if (!incidentType) {
    return settings.defaultMinutes;
  }

  const key = String(incidentType).trim().toLowerCase();
  const sourceKey = source ? String(source).trim().toLowerCase() : '';

  // Special case: Service Call source with Assist Motorist = 10 hours (600 minutes)
  if (key === 'assist motorist' && sourceKey === 'service call') {
    return 600;
  }

  return settings.overrides[key] ?? settings.defaultMinutes;
};

// Both dashboards derive prompts from current canonical calls and acknowledgments.
// Pinning may choose among overdue calls; it cannot revive an acknowledged,
// closed, archived, missing or reassigned call.
export function buildStatusCheckState({ incidents, getThreshold, acknowledgements = {}, rangerId, pinnedIncidentId, now = Date.now() }) {
  const incidentIds = [], rangers = new Set();
  const currentRanger = String(rangerId || '').toUpperCase();
  let modalCandidate = null;
  for (const incident of incidents) {
    if (!incident?.occurredAtUtc || incident.isArchived || incident.occurredEndUtc ||
        ['closed', 'cleared'].includes(String(incident.status || '').trim().toLowerCase())) continue;
    const started = Date.parse(incident.occurredAtUtc), threshold = Number(getThreshold(incident.incidentType, incident.source));
    if (!Number.isFinite(started) || !Number.isFinite(threshold)) continue;
    const minutes = (now - started) / 60000;
    if (minutes < threshold) continue;
    const savedAck = Number(acknowledgements[String(incident.id)]);
    const localAck = Number.isFinite(savedAck) ? savedAck : 0;
    const remoteAck = Date.parse(incident.statusCheckAcknowledgedUtc || '') || 0;
    if (Math.max(localAck, remoteAck) > 0 && (now - Math.max(localAck, remoteAck)) / 60000 < threshold) continue;
    incidentIds.push(incident.id);
    for (const id of [...(Array.isArray(incident.rangerIds) ? incident.rangerIds : []), incident.primaryRangerId]) if (id) rangers.add(String(id).toUpperCase());
    if (currentRanger && String(incident.primaryRangerId || '').toUpperCase() === currentRanger &&
        (!modalCandidate || incident.id === pinnedIncidentId)) modalCandidate = { incident, minutes: Math.max(1, Math.floor(minutes)) };
  }
  return { incidentIds, rangerIds: [...rangers].sort(), modalCandidate };
}
